'use client';

/**
 * Panel-native search (rail-redesign, wireframe-matched). Unlike the legacy
 * `SearchEverything` overlay, this lives INSIDE the left panel: a padded input,
 * always-visible scope chips (This workspace · Library · Everywhere), and
 * grouped results — Commands (search is also the command surface), On this
 * canvas, Apps (+ See all), Agents — with a footer hint. The ⌘K palette
 * (headless SearchEverything) is a separate surface and is left untouched.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useT } from '@/components/i18n-provider';
import { AppWindow, Plus } from 'lucide-react';
import { useReactFlow } from '@xyflow/react';
import { BotIcon } from '@/components/icons/bot-icon';
import { Avatar } from '@/components/avatar';
import { Spinner } from '@/components/ui/spinner';
import {
  useWorkspaceActions,
  useWorkspaceItems,
} from '@/components/workspace/store';
import {
  resolveItemLabel,
  useLiveContentMeta,
} from '@/components/workspace/item-label';
import { useWorkspaceCardActions } from '@/components/workspace/card-actions-context';
import { openPlansDialog } from '@/components/plans-dialog-host';
import { useAuth } from '@/components/auth-provider';
import { emitWorkspaceListChanged } from '@/lib/workspaces/list-events';
import { useRouter } from 'next/navigation';
import { openWorkspacePanel } from './panel-events';
import { openCreateWithDialog } from '@/components/workspace/create-with-dialog';
import { PanelSearchInput, panelChip } from './panel-ui';
import { detectPlatform, formatShortcut } from '@/lib/workspace/commands';
import { cn } from '@/lib/utils';
import { mergeAppHits, type AppHit, type MarketChannelHit } from './merge-app-hits';

type Scope = 'workspace' | 'library' | 'everywhere';

// Discovery-forward: Everywhere leads (and is the default) so apps/agents
// surface without a scope switch; the narrowing scopes follow.
const SCOPE_CHIPS: { id: Scope; label: string }[] = [
  { id: 'everywhere', label: 'Everywhere' },
  { id: 'library', label: 'My Content' },
  { id: 'workspace', label: 'This workspace' },
];

type AgentHit = {
  id: string;
  username: string;
  displayName?: string | null;
  avatarUrl?: string | null;
};

function Group({
  label,
  action,
  children,
}: {
  label: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="pb-1.5">
      <div className="flex items-center justify-between px-2 pt-2 pb-0.5">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          {label}
        </span>
        {action}
      </div>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}

function Row({
  leading,
  title,
  subtitle,
  trailing,
  onClick,
}: {
  leading: React.ReactNode;
  title: string;
  subtitle?: string;
  trailing?: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-accent/50"
    >
      <span className="flex h-7 w-7 shrink-0 items-center justify-center overflow-hidden rounded-md bg-foreground/[0.06] text-muted-foreground">
        {leading}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13px] text-foreground">{title}</span>
        {subtitle ? (
          <span className="block truncate text-[11px] text-muted-foreground">{subtitle}</span>
        ) : null}
      </span>
      {trailing}
    </button>
  );
}

export function SearchView() {
  const t = useT();
  const [q, setQ] = useState('');
  const [scope, setScope] = useState<Scope>('everywhere');
  const flow = useReactFlow();
  const wsItems = useWorkspaceItems();
  // "On this canvas" rows are named, and matched, live-first through
  // item-label.ts, the rail's rule: they read the snapshot stored in the
  // shared item, so a renamed app was listed, and only found, under its old
  // name (the 2026-09-13 browser pass). Read-only.
  const live = useLiveContentMeta();
  const { addItem, findFirstByRef } = useWorkspaceActions();
  const cardActions = useWorkspaceCardActions();
  const { user } = useAuth();
  const router = useRouter();
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const term = q.trim().toLowerCase();

  // --- Commands (search is the command surface) ----------------------------
  const newWorkspace = useCallback(async () => {
    try {
      const res = await fetch('/api/workspaces', { method: 'POST' });
      if (!res.ok) {
        // Per-plan workspace cap → open the Plans dialog instead of
        // silently no-oping (matches create-fab + workspaces-tab).
        if (res.status === 403) {
          const j = await res.clone().json().catch(() => null);
          if (j?.code === 'upgrade_required') openPlansDialog();
        }
        return;
      }
      const data = await res.json();
      emitWorkspaceListChanged();
      if (data?.workspace?.id) router.push(`/workspace/${data.workspace.id}`);
    } catch {
      /* gate owns the dialog */
    }
  }, [router]);

  const commands = useMemo(
    () => [
      {
        id: 'create-app',
        label: 'Create app',
        keywords: 'create app new widget build',
        icon: <Plus className="h-4 w-4" />,
        run: () => openCreateWithDialog('app'),
      },
      {
        id: 'create-agent',
        label: 'Create agent',
        keywords: 'create agent new bot build',
        icon: <BotIcon className="h-4 w-4" />,
        run: () => openCreateWithDialog('agent'),
      },
      // Camera commands (Zoom to fit / Reset zoom) were dropped from the
      // palette: canvas zoom is a fixed surface now (gesture zoom is
      // disabled), so zoom verbs here just read as clutter. Deliberate
      // camera actions live in the canvas right-click menu ("Fit to
      // view" / "Reset zoom"), their natural home.
      {
        id: 'new-workspace',
        label: 'New workspace',
        keywords: 'new workspace space create',
        icon: <Plus className="h-4 w-4" />,
        run: () => void newWorkspace(),
      },
    ],
    [newWorkspace],
  );

  const matchedCommands = useMemo(
    () =>
      commands.filter(
        (c) => !term || `${c.label} ${c.keywords}`.toLowerCase().includes(term),
      ),
    [commands, term],
  );

  // --- On this canvas (client-side over the live items) --------------------
  const canvasMatches = useMemo(() => {
    const out: Array<{ id: string; type: string; title: string; subtitle?: string; seed?: string; avatarUrl?: string | null }> = [];
    for (const it of wsItems) {
      const cache = (it.state?.cache ?? {}) as Record<string, unknown>;
      const title = resolveItemLabel(it, live, it.type);
      if (term && !title.toLowerCase().includes(term)) continue;
      out.push({
        id: it.id,
        type: it.type,
        title,
        subtitle: it.type === 'agent' && cache.username ? `@${cache.username as string}` : undefined,
        seed: (cache.username as string) || title,
        avatarUrl: (cache.avatarUrl as string) ?? null,
      });
      if (out.length >= 6) break;
    }
    return out;
  }, [wsItems, term, live]);

  const focusCanvasItem = useCallback(
    (id: string) => {
      const it = wsItems.find((x) => x.id === id);
      if (!it) return;
      flow.setCenter(
        it.state.x + it.state.width / 2,
        it.state.y + it.state.height / 2,
        { zoom: flow.getZoom(), duration: 280 },
      );
    },
    [wsItems, flow],
  );

  // --- Apps + Agents (remote, scope-gated, debounced) ----------------------
  const [apps, setApps] = useState<AppHit[]>([]);
  const [agents, setAgents] = useState<AgentHit[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (scope === 'workspace') {
      setApps([]);
      setAgents([]);
      setLoading(false);
      return;
    }
    const ctrl = new AbortController();
    setLoading(true);
    const enc = encodeURIComponent(term);
    const mine = scope === 'library' && user?.id;
    const appsUrl = mine
      ? `/api/posts?scope_mine=${user.id}&content_type=widget&search=${enc}&limit=6`
      : `/api/posts?content_type=widget&search=${enc}&limit=6`;
    // Marketplace APPS are channels (//apps/<category>/<app>), not widget
    // posts — search the same corpus the Apps tab browses so "See all in
    // Apps" agrees with the hits, then backfill remaining slots with widget
    // posts (legacy publishes without a listing channel). Library scope stays
    // posts-only: "My Content" means things you published.
    const channelsUrl =
      scope === 'everywhere' ? `/api/marketplace/channels?root=apps&q=${enc}&limit=6` : null;
    const agentsUrl =
      scope === 'library'
        ? `/api/users/me/agents?search=${enc}&limit=6`
        : `/api/discovery/agents?search=${enc}&limit=6`;
    const json = (u: string) =>
      fetch(u, { signal: ctrl.signal })
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null);
    const timer = setTimeout(() => {
      void Promise.all([
        json(appsUrl),
        json(agentsUrl),
        channelsUrl ? json(channelsUrl) : Promise.resolve(null),
      ]).then(([a, g, c]) => {
        if (ctrl.signal.aborted) return;
        // One row per app: a post folds into the channel that lists the
        // same app by content id, by source post, or by maker + name
        // (merge-app-hits.ts says why the content id alone was not enough).
        setApps(
          mergeAppHits(
            (c?.items as MarketChannelHit[]) ?? [],
            (a?.posts as AppHit[]) ?? [],
            6,
          ),
        );
        setAgents(((g?.agents as AgentHit[]) ?? []).slice(0, 6));
        setLoading(false);
      });
    }, 200);
    return () => {
      clearTimeout(timer);
      ctrl.abort();
    };
  }, [term, scope, user?.id]);

  const addApp = useCallback(
    (a: AppHit) => {
      if (!a.widgetContentId) return;
      const existing = findFirstByRef(a.widgetContentId);
      if (existing) {
        flow.setCenter(
          existing.state.x + existing.state.width / 2,
          existing.state.y + existing.state.height / 2,
          { zoom: flow.getZoom(), duration: 280 },
        );
        return;
      }
      cardActions?.addWidget({
        contentId: a.widgetContentId,
        title: a.title ?? 'App',
        thumbnailUrl: a.thumbnailUrl ?? null,
        metadata: null,
      });
    },
    [cardActions, findFirstByRef, flow],
  );

  const addAgent = useCallback(
    (g: AgentHit) => {
      const existing = findFirstByRef(g.id);
      if (existing) {
        flow.setCenter(
          existing.state.x + existing.state.width / 2,
          existing.state.y + existing.state.height / 2,
          { zoom: flow.getZoom(), duration: 280 },
        );
        return;
      }
      void addItem({
        type: 'agent',
        refId: g.id,
        state: {
          cache: {
            username: g.username,
            displayName: g.displayName ?? g.username,
            avatarUrl: g.avatarUrl ?? null,
          },
        },
      }).catch(() => {});
    },
    [addItem, findFirstByRef, flow],
  );

  const showRemote = scope !== 'workspace';
  const nothing =
    matchedCommands.length === 0 &&
    canvasMatches.length === 0 &&
    apps.length === 0 &&
    agents.length === 0 &&
    !loading;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Input + scope chips */}
      <div className="px-3 pt-1 pb-2">
        <PanelSearchInput
          inputRef={inputRef}
          value={q}
          onChange={setQ}
          placeholder={t('workspace.panels.searchEverything')}
          // ⌘K, the chord that opens this view. It read ⌘⇧K until
          // 2026-10-03, which is Discover's chord (search-chord.ts): the
          // browser pass that day found this view naming a shortcut that
          // opens something else.
          hint={formatShortcut({ key: 'k', mod: true }, detectPlatform())}
        />
        <div className="flex gap-1.5 pt-2">
          {SCOPE_CHIPS.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setScope(s.id)}
              className={panelChip(scope === s.id)}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Results */}
      <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-1">
        {matchedCommands.length > 0 && (
          <Group label={t('workspace.panels.commands')}>
            {matchedCommands.map((c) => (
              <Row
                key={c.id}
                leading={c.icon}
                title={c.label}
                onClick={c.run}
              />
            ))}
          </Group>
        )}

        {canvasMatches.length > 0 && (
          <Group label={t('workspace.panels.onThisCanvas')}>
            {canvasMatches.map((m) => (
              <Row
                key={m.id}
                leading={
                  m.type === 'agent' ? (
                    <Avatar seed={m.seed || m.title} type="agent" src={m.avatarUrl ?? undefined} className="h-7 w-7" />
                  ) : (
                    <AppWindow className="h-4 w-4" />
                  )
                }
                title={m.title}
                subtitle={m.subtitle}
                onClick={() => focusCanvasItem(m.id)}
              />
            ))}
          </Group>
        )}

        {showRemote && apps.length > 0 && (
          <Group
            label={t('discover.widgets')}
            action={
              <button
                type="button"
                onClick={() => openWorkspacePanel('left', { tab: 'apps' })}
                className="text-[11px] font-medium text-primary hover:underline"
              >
                {t('workspace.panels.seeAllApps')}
              </button>
            }
          >
            {apps.map((a) => (
              <Row
                key={a.id}
                leading={
                  a.thumbnailUrl ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img src={a.thumbnailUrl} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <AppWindow className="h-4 w-4" />
                  )
                }
                title={a.title ?? 'App'}
                subtitle={cn(
                  a.category
                    ? a.category
                    : a.author?.username
                      ? `@${a.author.username}`
                      : 'Public',
                  a.installCount ? ` · ${a.installCount} uses` : '',
                )}
                onClick={() => addApp(a)}
                trailing={
                  <span className="inline-flex shrink-0 items-center gap-1 rounded-md border border-border/60 px-2 py-1 text-[11px] font-medium text-foreground transition-colors group-hover:bg-accent">
                    <Plus className="h-3 w-3" /> Add
                  </span>
                }
              />
            ))}
          </Group>
        )}

        {showRemote && agents.length > 0 && (
          <Group
            label={t('workspace.canvas.tabs.agents')}
            action={
              <button
                type="button"
                onClick={() => openWorkspacePanel('left', { tab: 'agents' })}
                className="text-[11px] font-medium text-primary hover:underline"
              >
                {t('workspace.panels.seeAllAgents')}
              </button>
            }
          >
            {agents.map((g) => (
              <Row
                key={g.id}
                leading={<Avatar seed={g.username} type="agent" src={g.avatarUrl ?? undefined} className="h-7 w-7" />}
                title={g.displayName || g.username}
                subtitle={`@${g.username}`}
                onClick={() => addAgent(g)}
              />
            ))}
          </Group>
        )}

        {loading && (
          <div className="flex justify-center py-4">
            <Spinner />
          </div>
        )}
        {nothing && (
          <div className="px-3 py-6 text-center text-xs text-muted-foreground">
            {term ? `Nothing matches “${q.trim()}”.` : 'Type to search, or switch scope to browse apps and agents.'}
          </div>
        )}
      </div>

      {/* No footer line (2026-10-03). It read "⌘⇧K opens this from anywhere,
          even inside apps. Commands live here too, so search is also the
          command surface.": a chord that is Discover's, and a sentence about
          how the product is built. The field's own hint names the chord. */}
    </div>
  );
}
